import {
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { ChevronRight, TriangleAlert } from 'lucide-react';
import { Fragment, useId } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import {
    formatDayText,
    formatPatientName,
    sexLabel,
} from '~/containers/patient-list/model';
import { PatientIdText } from '~/containers/patient-list/shared';
import { dicomDateToIso } from '~/domain/dicom/importScan';
import {
    remotePatientKey,
    type RemotePatientIdentity,
    type SourcedPatient,
    type SourceSearch,
} from '../model';
import {
    PacsBodyCell,
    PacsColgroup,
    PacsCompactRow,
    PacsGroupCell,
    PacsHeadCell,
    PACS_TABLE_SX,
    type PacsColumn,
} from './pacsTable';

/**
 * What a lookup matched, one line per patient, under a heading per source when more than one source
 * was searched.
 *
 * The identity a retrieval is keyed by is read off this list, so every part of it the source
 * reported keeps its own column rather than being run together into one meta line: two people who
 * share a name are told apart by the birth date beside it, and a column the user has to parse a
 * joined string to find is one they stop checking.
 *
 * An id that two sources give to different people is marked on every row carrying it. Those rows
 * are never merged: picking one reads the series of that person only.
 */

export type SourceResults = {
    readonly source: string;
    readonly search: SourceSearch;
    /** The source did not answer the reachability probe when the dialog opened. */
    readonly unreachable: boolean;
};

type PacsPatientTableProps = {
    groups: readonly SourceResults[];
    /** Headings name each source; with one source searched there is nothing to tell apart. */
    showSourceHeadings: boolean;
    conflictingIds: ReadonlySet<string>;
    onSelect: (picked: SourcedPatient) => void;
};

function columns(intl: IntlShape): readonly PacsColumn[] {
    return [
        {
            id: 'patient',
            label: intl.formatMessage({
                id: 'patientList.import.pacs.column.patient',
            }),
            width: 'flex',
        },
        {
            id: 'patientId',
            label: intl.formatMessage({
                id: 'patientList.import.pacs.column.patientId',
            }),
            width: 130,
        },
        {
            id: 'sex',
            label: intl.formatMessage({
                id: 'patientList.import.pacs.column.sex',
            }),
            width: 44,
        },
        {
            id: 'birthDate',
            label: intl.formatMessage({
                id: 'patientList.import.pacs.column.birthDate',
            }),
            width: 92,
        },
        {
            id: 'studies',
            label: intl.formatMessage({
                id: 'patientList.import.pacs.column.studies',
            }),
            width: 54,
        },
        { id: 'open', width: 20 },
    ];
}

export default function PacsPatientTable({
    groups,
    showSourceHeadings,
    conflictingIds,
    onSelect,
}: PacsPatientTableProps) {
    const intl = useIntl();
    const cols = columns(intl);
    // Every row describes itself with this one node. A row is named by its cells, so two matches
    // read apart; what activating one does is the same for all of them and is a description.
    const activationHintId = useId();
    return (
        <>
            <span id={activationHintId} hidden>
                {intl.formatMessage({
                    id: 'patientList.import.pacs.showSeries',
                })}
            </span>
            <Table stickyHeader size="small" sx={PACS_TABLE_SX}>
                <PacsColgroup columns={cols} />
                <TableHead>
                    <TableRow>
                        {cols.map((column) => (
                            <PacsHeadCell key={column.id}>
                                {column.label}
                            </PacsHeadCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {groups.map((group) => (
                        <Fragment key={group.source}>
                            {showSourceHeadings && (
                                <TableRow>
                                    <PacsGroupCell
                                        colSpan={cols.length}
                                        // Below the sticky head row rather than under it.
                                        sx={{ top: 36 }}
                                    >
                                        <SourceHeading group={group} />
                                    </PacsGroupCell>
                                </TableRow>
                            )}
                            {group.search.patients.map((patient) => (
                                <PatientResultRow
                                    key={remotePatientKey(patient)}
                                    patient={patient}
                                    stale={group.search.stale}
                                    conflicting={conflictingIds.has(
                                        patient.dicomPatientId
                                    )}
                                    activationHintId={activationHintId}
                                    onSelect={() =>
                                        onSelect({
                                            source: group.source,
                                            patient,
                                        })
                                    }
                                />
                            ))}
                        </Fragment>
                    ))}
                </TableBody>
            </Table>
        </>
    );
}

/** A source's name and where its answer stands. */
function SourceHeading({ group }: { group: SourceResults }) {
    const intl = useIntl();
    const { search } = group;
    const status =
        search.status === 'searching'
            ? intl.formatMessage({
                  id: 'patientList.import.pacs.source.searching',
              })
            : search.status === 'failed'
              ? intl.formatMessage({
                    id: group.unreachable
                        ? 'patientList.import.pacs.source.unreachable'
                        : 'patientList.import.pacs.source.failed',
                })
              : intl.formatMessage(
                    {
                        id: search.truncated
                            ? 'patientList.import.pacs.source.matchesTruncated'
                            : 'patientList.import.pacs.source.matches',
                    },
                    { count: search.patients.length }
                );
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography component="span" sx={{ fontSize: 12, fontWeight: 600 }}>
                {group.source}
            </Typography>
            {search.status === 'searching' && (
                <CircularProgress size={10} aria-hidden />
            )}
            <Typography
                component="span"
                sx={{
                    fontSize: 12,
                    color:
                        search.status === 'failed'
                            ? 'error.main'
                            : 'text.secondary',
                }}
            >
                {status}
            </Typography>
        </Box>
    );
}

function PatientResultRow({
    patient,
    stale,
    conflicting,
    activationHintId,
    onSelect,
}: {
    patient: RemotePatientIdentity;
    /** The row answers an earlier term; it stays for reading until the source answers again. */
    stale: boolean;
    conflicting: boolean;
    activationHintId: string;
    onSelect: () => void;
}) {
    const intl = useIntl();
    const name = formatPatientName(patient.dicomPatientName);
    // Rendered through the app's own date formatter rather than as the wire's digits, so a birth
    // date reads the same here as everywhere else it is shown.
    const birthDate = formatDayText(
        intl,
        dicomDateToIso(patient.patientBirthDate)
    );
    return (
        <PacsCompactRow
            isSelected={false}
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={(event) => {
                if (event.currentTarget !== event.target) {
                    return;
                }
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect();
                }
            }}
            aria-describedby={activationHintId}
            sx={{
                opacity: stale ? 0.55 : 1,
                '&:hover .pacs-chevron, &:focus-visible .pacs-chevron': {
                    color: 'text.secondary',
                },
            }}
        >
            <PacsBodyCell>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        minWidth: 0,
                    }}
                >
                    {conflicting && (
                        <Tooltip
                            title={intl.formatMessage({
                                id: 'patientList.import.pacs.identityConflict',
                            })}
                        >
                            <Box
                                component="span"
                                role="img"
                                aria-label={intl.formatMessage({
                                    id: 'patientList.import.pacs.identityConflict',
                                })}
                                sx={{
                                    display: 'inline-flex',
                                    color: 'warning.main',
                                    flexShrink: 0,
                                }}
                            >
                                <TriangleAlert size={13} />
                            </Box>
                        </Tooltip>
                    )}
                    <Typography
                        component="span"
                        noWrap
                        sx={{
                            display: 'block',
                            minWidth: 0,
                            fontSize: 13,
                            fontWeight: 500,
                        }}
                    >
                        {name ? (
                            <SensitiveInfoTypography>
                                {name}
                            </SensitiveInfoTypography>
                        ) : (
                            intl.formatMessage({
                                id: 'patientList.import.pacs.unnamedPatient',
                            })
                        )}
                    </Typography>
                </Box>
            </PacsBodyCell>
            <PacsBodyCell>
                <PatientIdText
                    id={patient.dicomPatientId}
                    tooltipPlacement="right"
                />
            </PacsBodyCell>
            {/* Sex is chrome beside the identifier rather than one itself, so it stays legible
                while the birth date is masked. */}
            <PacsBodyCell>
                <Typography variant="monoMeta">
                    {sexLabel(intl, patient.patientSex)}
                </Typography>
            </PacsBodyCell>
            <PacsBodyCell>
                <Typography variant="monoMeta">
                    <SensitiveInfoTypography>
                        {birthDate}
                    </SensitiveInfoTypography>
                </Typography>
            </PacsBodyCell>
            <PacsBodyCell>
                <Typography variant="monoMeta">
                    {patient.studyCount ?? ''}
                </Typography>
            </PacsBodyCell>
            <PacsBodyCell>
                <Box
                    component="span"
                    aria-hidden
                    className="pacs-chevron"
                    sx={{
                        display: 'inline-flex',
                        color: 'action.disabled',
                        transition: 'color 100ms',
                    }}
                >
                    <ChevronRight size={14} />
                </Box>
            </PacsBodyCell>
        </PacsCompactRow>
    );
}
